import React, { useEffect, useState } from 'react'
import { Button, Typography } from '@mui/material'
import KeyIcon from '@mui/icons-material/Key'
import * as S from './styles'
import { Container } from 'src/ui'
import { Formik, Field, Form } from 'formik'
import { TextField } from 'formik-mui'
import useLogin, { initialValues, validate } from './Login.State'
import ForgotPassword from './ForgotPassword'
import MessageSent from './MessageSent'
import MobileDesktopNotification from '../MobileDesktopNotification'
import SetupMfa from '../Mfa/SetupMfa'
import { useAuth } from 'src/common/context'
import { useNavigate } from 'react-router-dom'
import { recordSigninEvent } from 'src/common/api/accountSecurity'
import { passkeyErrorMessage } from 'src/common/context/auth/passkeyErrors'

const Login: React.FC = () => {
  const { state, onSubmit } = useLogin()
  const { user, isPasskeySupported, signInWithPasskey } = useAuth()

  const [modals, setModals] = useState({
    forgotPassword: false,
    messageSent: false
  })
  const [passkeyError, setPasskeyError] = useState<string>('')
  const [passkeyLoading, setPasskeyLoading] = useState(false)
  const showPasskey = isPasskeySupported()
  // The email + password fields are hidden until "Log in with a password" is clicked. If passkeys aren't
  // supported, show the fields immediately (no extra click).
  const [showPassword, setShowPassword] = useState(() => !isPasskeySupported())

  const navigate = useNavigate()

  const handleClose = (modal: string) => {
    setModals((s) => ({ ...s, [modal]: false }))
  }

  const handleOpen = (modal: string) => {
    setModals((s) => ({ ...s, [modal]: true }))
  }

  const handleKeyDown = (event: KeyboardEvent, submitForm: () => Promise<void>) => {
    if (event.key === 'Enter') {
      submitForm()
    }
  }

  // Usernameless passkey sign-in (uses the remembered email; the email field is hidden here). Cancel/timeout/
  // failure is non-blocking and never disables the password path.
  const handlePasskeyLogin = async () => {
    setPasskeyError('')
    setPasskeyLoading(true)
    try {
      const result = await signInWithPasskey()
      if (result.isSignedIn) {
        recordSigninEvent('passkey').catch(() => undefined)
        navigate('/')
      } else {
        // eslint-disable-next-line no-console
        console.warn('Passkey sign-in did not complete. nextStep:', result.nextStep)
        setPasskeyError('Passkey sign-in could not be completed on this account. Sign in with your password instead.')
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Passkey sign-in error:', e)
      if ((e as Error).message === 'NO_REMEMBERED_EMAIL') {
        setPasskeyError('Sign in with your password once on this device first, then you can use your passkey.')
      } else {
        setPasskeyError(passkeyErrorMessage(e, 'signin'))
      }
    } finally {
      setPasskeyLoading(false)
    }
  }

  useEffect(() => {
    if (user) navigate('/')
  }, [user])

  // check if user is on a mobile device
  const details = navigator.userAgent
  const regexp = /android|iphone|kindle|ipad/i
  const isMobileDevice = regexp.test(details)

  if (isMobileDevice) {
    return <MobileDesktopNotification />
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validate={validate}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={onSubmit}
      >
        {({ submitForm, isSubmitting }) => (
          <Form>
            <Container title="Log in to your account" error={state.error && state.error}>
              {showPasskey && (
                <>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<KeyIcon />}
                    loading={passkeyLoading}
                    onClick={handlePasskeyLogin}
                  >
                    Log in with a passkey
                  </Button>
                  {passkeyError && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      {passkeyError}
                    </Typography>
                  )}
                </>
              )}

              {!showPassword ? (
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    mt: 2,
                    backgroundColor: 'common.white',
                    '&:hover': { backgroundColor: 'common.white' }
                  }}
                  onClick={() => setShowPassword(true)}
                >
                  Log in with a password
                </Button>
              ) : (
                <>
                  <S.Row sx={showPasskey ? { mt: 3 } : undefined}>
                    <S.InputLabel>Email *</S.InputLabel>
                    <Field component={TextField} name="email" placeholder="Email" fullWidth />
                  </S.Row>
                  <S.InputLabel>Password *</S.InputLabel>
                  <Field
                    component={TextField}
                    name="password"
                    type="password"
                    placeholder="Password"
                    fullWidth
                    onKeyDown={(e: KeyboardEvent) => handleKeyDown(e, submitForm)}
                  />
                  <S.ActionsRow>
                    <S.StyledLink component="a" onClick={() => handleOpen('forgotPassword')}>
                      Forgot Password?
                    </S.StyledLink>
                    <S.Button variant="contained" loading={isSubmitting} onClick={submitForm}>
                      Log In
                    </S.Button>
                  </S.ActionsRow>
                </>
              )}
            </Container>
          </Form>
        )}
      </Formik>
      <ForgotPassword open={modals.forgotPassword} handleClose={handleClose} handleOpen={handleOpen} />
      <MessageSent open={modals.messageSent} handleClose={handleClose} />
      {state.setupMfa && <SetupMfa open={state.setupMfa} setupMfaParams={state.setupMfaParams} enforced />}
    </>
  )
}

export default Login
